'use client';

import { JSX, useMemo } from 'react';

import { readableColor } from 'polished';

import { useTheme } from 'contexts/ThemeContext';
import { VariantLogoProps } from './variants';
import * as Full from './variants/Full';
import * as Icon from './variants/Icon';
import * as Text from './variants/Text';

import styles from './styles.module.css';

type Variant = 'text' | 'icon' | 'full';
type VariantLogo = (props: VariantLogoProps) => JSX.Element;

interface LogoProps {
	variant: Variant;
	width: number;
	fill?: string & 'auto';
}

const variants: Record<Variant, VariantLogo> = {
	full: (props: VariantLogoProps) => <Full.Component {...props} />,
	text: (props: VariantLogoProps) => <Text.Component {...props} />,
	icon: (props: VariantLogoProps) => <Icon.Component {...props} />,
};

const factors: Record<Variant, number> = {
	full: Full.factor,
	text: Text.factor,
	icon: Icon.factor,
};

function Logo({ width, variant, fill = 'auto' }: LogoProps) {
	// Hooks
	const theme = useTheme();

	// Memo vars
	const sizes = useMemo(() => {
		const factor = factors[variant];

		const height = factor * width;

		return { width, height };
	}, [variant, width]);

	const color = useMemo(() => {
		if (fill === 'auto') return readableColor(theme.colors.background);

		return fill;
	}, [fill, theme.colors.background]);

	const VariantLogo = useMemo(() => variants[variant], [variant]);

	return (
		<div
			className={styles.logo}
			style={{
				width: `${sizes.width / 16}rem`,
				height: `${sizes.height / 16}rem`,
			}}
		>
			<VariantLogo
				width={sizes.width}
				height={sizes.height}
				fill={color}
			/>
		</div>
	);
}

export default Logo;
